"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useElement = void 0;
var _react = require("react");
var _useBasisTheoryValue = require("./useBasisTheoryValue");
const elementHasSetValueRef = val => Boolean(val.setValueRef);
const shallowDifference = (objA, objB) => [...new Set([...Object.keys(objA || {}), ...Object.keys(objB || {})])].reduce((changed, key) => {
  const aValue = objA === null || objA === void 0 ? void 0 : objA[key];
  const bValue = objB === null || objB === void 0 ? void 0 : objB[key];
  if (aValue !== bValue) {
    return {
      ...changed,
      [key]: bValue
    };
  }
  return changed;
}, {});

/**
 * Creates, mounts and indexes an Element
 * with React lifecycle
 * @param id
 * @param type
 * @param options
 * @param wrapperRef
 * @param btFromProps
 * @param ref optional ref to set the underlying element
 * @returns created element and initial options used for mounting
 */
const useElement = (id, type, wrapperRef, options, btFromProps, ref, targetValueRef) => {
  const bt = (0, _useBasisTheoryValue.useBasisTheoryValue)(btFromProps);
  const [lastOptions, setLastOptions] = (0, _react.useState)();
  const elementRef = (0, _react.useRef)(null);
  (0, _react.useEffect)(() => {
    if (bt && wrapperRef.current && !elementRef.current) {
      const newElement = bt.createElement(type, options); // conversion to unknown necessary because of different value prop types

      elementRef.current = newElement;
      if (typeof ref === 'function') {
        ref(newElement);
      }
      if (ref && typeof ref === 'object') {
        // eslint-disable-next-line no-param-reassign
        ref.current = newElement;
      }
      (async () => {
        await newElement.mount(`#${id}`).catch(mountError => {
          setLastOptions(() => {
            throw mountError;
          });
        });
        if (elementHasSetValueRef(newElement) && targetValueRef !== null && targetValueRef !== void 0 && targetValueRef.current) {
          newElement.setValueRef(targetValueRef.current);
        }
      })();
      bt.indexElement(id, newElement);
      setLastOptions(options);
    }

    // the only two dependencies that we need to watch for
    // are bt and wrapperRef. Anything else changing should not
    // be considered for creating/mounting an element
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bt, wrapperRef]);
  (0, _react.useEffect)(() => {
    if (elementRef.current && lastOptions && options !== lastOptions) {
      const optionsDifference = shallowDifference(lastOptions, options);
      if (Object.keys(optionsDifference).length) {
        setLastOptions(options);
        elementRef.current.update(optionsDifference).catch(updateError => {
          setLastOptions(() => {
            throw updateError;
          });
        });
      }
    }
  }, [options, lastOptions]);
  return (elementRef === null || elementRef === void 0 ? void 0 : elementRef.current) || undefined;
};
exports.useElement = useElement;